import { Box, Divider } from '@chakra-ui/react';
import { Cart } from '@diamond/shared/types';
import React, { useMemo } from 'react';

import {
  CartDivisionSection,
  CartDivisionSelectionState,
} from './CartDivisionSection';
import { CartPromosSummary } from './CartPromosSummary';
import { VoucherBannerSummary } from './VoucherBannerSummary';

interface CartListProps {
  groupedByDivision: Record<string, Cart[]>;
  renderItem: (item: Cart) => React.ReactNode;
  selectedIds: Set<string>;
  onToggleItems: (itemIds: string[], action: 'select' | 'deselect') => void;
  isLoading?: boolean;
}

const CartListComponent = ({
  groupedByDivision,
  renderItem,
  selectedIds,
  onToggleItems,
  isLoading,
}: CartListProps) => {
  const allCartItems = useMemo(
    () => Object.values(groupedByDivision).flat(),
    [groupedByDivision]
  );
  const { divisionSelectionState, divisionSelectableIds } = useMemo(() => {
    const selectionState: Record<string, CartDivisionSelectionState> = {};
    const selectableIds: Record<string, string[]> = {};

    Object.entries(groupedByDivision).forEach(([division, items]) => {
      const selectableItems = items.filter(
        (cartItem) => cartItem.product.stock !== 0
      );
      const totalSelectable = selectableItems.length;

      if (totalSelectable === 0) {
        selectionState[division] = {
          isChecked: false,
          isIndeterminate: false,
          isDisabled: true,
        };
        selectableIds[division] = [];
        return;
      }

      const selectedCount = selectableItems.reduce((count, cartItem) => {
        return selectedIds.has(cartItem.id) ? count + 1 : count;
      }, 0);

      selectionState[division] = {
        isChecked: selectedCount === totalSelectable,
        isIndeterminate: selectedCount > 0 && selectedCount < totalSelectable,
        isDisabled: false,
      };
      selectableIds[division] = selectableItems.map((cartItem) => cartItem.id);
    });

    return {
      divisionSelectionState: selectionState,
      divisionSelectableIds: selectableIds,
    };
  }, [groupedByDivision, selectedIds]);

  const handleToggleDivision = (division: string) => {
    const selectableIds = divisionSelectableIds[division] || [];
    if (selectableIds.length === 0) {
      return;
    }

    const currentSelection = divisionSelectionState[division];
    if (currentSelection?.isChecked) {
      onToggleItems(selectableIds, 'deselect');
    } else {
      onToggleItems(selectableIds, 'select');
    }
  };

  return (
    <>
      <Box>
        {Object.entries(groupedByDivision).map(([division, items], index) => (
          <React.Fragment key={division}>
            {index > 0 && (
              <Divider
                display={{ base: 'block', lg: 'none' }}
                borderColor="#E5E5EA"
              />
            )}
            <CartDivisionSection
              division={division}
              items={items}
              selection={divisionSelectionState[division]}
              onToggleDivision={() => handleToggleDivision(division)}
            >
              {items.map((item, itemIndex) => (
                <React.Fragment key={item.id}>
                  {itemIndex > 0 && (
                    <Divider
                      display={{ base: 'block', lg: 'none' }}
                      borderColor="#E5E5EA"
                    />
                  )}
                  <div>{renderItem(item)}</div>
                </React.Fragment>
              ))}
            </CartDivisionSection>
          </React.Fragment>
        ))}
      </Box>
      <CartPromosSummary
        cartItems={allCartItems}
        selectedIds={selectedIds}
        isLoading={isLoading}
      />
      <VoucherBannerSummary
        selectedIds={selectedIds}
        cartItems={allCartItems}
      />
    </>
  );
};

export const CartList = React.memo(CartListComponent);
